import clsx from 'clsx'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useElementBoundingClientRectObserver } from 'hooks/useElementSize'
import useHasMounted from 'hooks/useHasMounted'
import isHTMLInputElement from 'lib/dom/isHTMLInputElement'
import createResponsiveClassNameGetter from 'lib/styles/createResponsiveClassNameGetter'
import getResponsiveStyles from 'lib/styles/getResponsiveStyles'
import { rem } from 'polished'
import type { FormEventHandler, PropsWithChildren } from 'react'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import styled, { css } from 'styled-components'
import type { SegmentedOptionsContextValue } from './contexts/SegmentedOptionsContext'
import SegmentedOptionsContext from './contexts/SegmentedOptionsContext'
import { _SegmentedOptionRoot } from './SegmentedOption'

const Container = styled.div``

const INDICATOR_TRANSITION = 'opacity 200ms, transform 200ms, height 200ms, width 200ms'

const OPTION_GROUPS_COMPACT_PADDING = 4
const OPTION_GROUPS_COMFORTABLE_PADDING = 8

type SegmentedOptionsGroupDensity = 'compact' | 'comfortable'

const getSegmentedOptionsIndicatorDensityClassName = createResponsiveClassNameGetter<SegmentedOptionsGroupDensity>('segmented-options-indicator-density')
const {
  mobile: indicatorDensityStyle,
  tablet: tabletIndicatorDensityStyle,
  desktop: desktopIndicatorDensityStyle,
} = getResponsiveStyles(getSegmentedOptionsIndicatorDensityClassName, {
  compact: css`
    transform: translate(${rem(OPTION_GROUPS_COMPACT_PADDING)}, ${rem(OPTION_GROUPS_COMPACT_PADDING)});
    height: calc(100% - ${rem(OPTION_GROUPS_COMPACT_PADDING * 2)});
  `,
  comfortable: css`
    transform: translate(${rem(OPTION_GROUPS_COMFORTABLE_PADDING)}, ${rem(OPTION_GROUPS_COMFORTABLE_PADDING)});
    height: calc(100% - ${rem(OPTION_GROUPS_COMFORTABLE_PADDING * 2)});
  `,
})

const Indicator = styled.div`
  z-index: 1;
  opacity: 0;
  width: 0;
  position: absolute;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  box-shadow: ${props => props.theme.shadow.flat.medium};
  border-radius: ${props => props.theme.borderRadius.round};
  transition: ${INDICATOR_TRANSITION};

  ${indicatorDensityStyle}

  ${mediaQueryUp.tablet} {
    ${tabletIndicatorDensityStyle}
  }

  ${mediaQueryUp.desktop} {
    ${desktopIndicatorDensityStyle}
  }
`

const getSegmentedOptionsGroupDensityClassName = createResponsiveClassNameGetter<SegmentedOptionsGroupDensity>('segmented-options-group-density')
const {
  mobile: densityStyle,
  tablet: tabletDensityStyle,
  desktop: desktopDensityStyle,
} = getResponsiveStyles(getSegmentedOptionsGroupDensityClassName, {
  compact: css`
    padding: ${rem(OPTION_GROUPS_COMPACT_PADDING)};
    grid-auto-columns: minmax(min-content, auto);
  `,
  comfortable: css`
    padding: ${rem(OPTION_GROUPS_COMFORTABLE_PADDING)};
    grid-auto-columns: minmax(min-content, 1fr);
  `,
})

const {
  mobile: optionDensityStyle,
  tablet: tabletOptionDensityStyle,
  desktop: desktopOptionDensityStyle,
} = getResponsiveStyles(getSegmentedOptionsGroupDensityClassName, {
  compact: css`
    > ${_SegmentedOptionRoot} {
      padding: ${rem(2)} ${rem(16)};
      height: auto;
      min-height: ${rem(32)};
      min-width: auto;
    }
  `,
  comfortable: css`
    > ${_SegmentedOptionRoot} {
      padding: ${rem(4)} ${rem(16)};
      height: auto;
      min-height: ${rem(44)};
      min-width: ${rem(80)};
    }
  `,
})

const OptionsGroup = styled.div`
  position: relative;
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  display: grid;
  grid-auto-flow: column;
  align-items: stretch;
  gap: ${rem(4)};
  border-radius: ${props => props.theme.borderRadius.round};
  overflow-x: auto;
  overflow-y: hidden;
  transform: translateZ(0); /** so it sits atop the indicator */

  ${densityStyle}
  ${optionDensityStyle}

  ${mediaQueryUp.tablet} {
    ${tabletDensityStyle}
    ${tabletOptionDensityStyle}
  }

  ${mediaQueryUp.desktop} {
    ${desktopDensityStyle}
    ${desktopOptionDensityStyle}
  }
`

export type SegmentedOptionsGroupOnValueChangeHandler<T = string> = (value: T) => void

interface Props<T> extends PropsWithChildren, Utils.PropsWithDataAttributes {
  className?: string
  /**
   * Sets the `name` attribute for every `SegmentedOption` child.
   */
  inputsName: string
  /**
   * @default comfortable
   */
  density?: SegmentedOptionsGroupDensity
  tabletDensity?: SegmentedOptionsGroupDensity
  desktopDensity?: SegmentedOptionsGroupDensity
  onValueChange?: SegmentedOptionsGroupOnValueChangeHandler<T>
}

function SegmentedOptionsGroup<T = string>({
  children,
  inputsName,
  density = 'comfortable',
  tabletDensity,
  desktopDensity,
  onValueChange,
  ...rest
}: Props<T>) {
  const context = useMemo<SegmentedOptionsContextValue>(() => ({ inputsName }), [inputsName])

  const fieldSetRef = useRef<HTMLFieldSetElement>(null)
  const indicatorRef = useRef<HTMLDivElement>(null)
  const attachIndicator = useCallback((element: HTMLInputElement) => {
    if (indicatorRef.current) {
      if (element.type === 'radio') {
        const inputLabel: HTMLLabelElement | undefined = element.labels?.[0]
        if (inputLabel) {
          const labelRects = inputLabel.getClientRects()
          for (const rect of labelRects) {
            indicatorRef.current.style.opacity = '1'
            indicatorRef.current.style.transform = `translate(${inputLabel.offsetLeft}px, ${inputLabel.offsetTop}px)`
            indicatorRef.current.style.height = `${rect.height}px`
            indicatorRef.current.style.width = `${rect.width}px`
          }
        }
      }
    }
  }, [])

  const handleChange = useCallback<FormEventHandler<HTMLFieldSetElement>>((event) => {
    if (isHTMLInputElement(event.target)) {
      attachIndicator(event.target)
      onValueChange?.(event.target.value as T)
    }
  }, [attachIndicator, onValueChange])

  const hasMounted = useHasMounted()

  const getCheckedInput = useCallback((): HTMLInputElement | undefined => {
    const element = fieldSetRef.current?.querySelector('input[type="radio"]:checked')
    return (!!element && isHTMLInputElement(element)) ? element : undefined
  }, [])

  const reattachIndicator = useCallback(() => {
    if (fieldSetRef.current) {
      const inputElement = getCheckedInput()
      if (inputElement) {
        attachIndicator(inputElement)
      }
    }
  }, [attachIndicator, getCheckedInput])

  useEffect(() => {
    if (hasMounted) {
      reattachIndicator()
    }
    // Needs to run after ever render, in case the selected option changed without
    // the user clicking one of the segments. Therefore, no dependency array.
  })

  useElementBoundingClientRectObserver(fieldSetRef, reattachIndicator)

  // Why the extra `<Container>`?
  // CSS on `fieldset` is slightly unpredictable, therefore adding an extra `<div>` layer,
  // so it can be styled safely.
  return <SegmentedOptionsContext value={context}>
    <Container {...rest}>
      <fieldset ref={fieldSetRef} onChange={handleChange}>
        <OptionsGroup
          className={clsx(getSegmentedOptionsGroupDensityClassName({ mobile: density, tablet: tabletDensity, desktop: desktopDensity }))}
        >
          <Indicator role="none" ref={indicatorRef} />
          {children}
        </OptionsGroup>
      </fieldset>
    </Container>
  </SegmentedOptionsContext>
}

export default SegmentedOptionsGroup
